import { useDroppable } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMemo, type ReactNode } from 'react'
import type { KanbanColumnApi, KanbanColumnData } from './types'

interface Props<TCard extends { id: string }> {
  column: KanbanColumnData<TCard>
  /** When true, the column itself participates in column-level reordering. */
  sortable?: boolean
  children: (api: KanbanColumnApi) => ReactNode
}

/**
 * Column shell. Wraps the consumer's render output in a card-level `SortableContext`
 * so any `KanbanCard`s rendered inside participate in vertical sorting. Exposes
 * column-level drag handle props, a droppable body ref, and `isOver`/`isDragging`
 * flags via the render-prop API.
 *
 * The SortableContext lives in this component (not in the API) to keep its identity
 * stable across renders — otherwise dnd-kit's internal state thrashes mid-drag,
 * which causes a render loop (React #185).
 */
export function KanbanColumn<TCard extends { id: string }>({
  column,
  sortable = false,
  children,
}: Props<TCard>) {
  const {
    setNodeRef: setSortableRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: !sortable })

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({ id: column.id })

  const dragHandleStyle: React.CSSProperties = useMemo(
    () =>
      sortable
        ? {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
          }
        : {},
    [sortable, transform, transition, isDragging]
  )

  const cardIds = useMemo(() => column.cards.map((c) => c.id), [column.cards])

  const api: KanbanColumnApi = {
    dragHandleProps: {
      ref: setSortableRef,
      style: dragHandleStyle,
      attributes,
      listeners,
    },
    droppableRef: setDroppableRef,
    isOver,
    isDragging,
  }

  return (
    <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
      {children(api)}
    </SortableContext>
  )
}
